import { Divider, FormControl, FormControlLabel, Grid, Radio, RadioGroup } from '@mui/material';
import { JudgmentDataForm } from 'cancellations/view-models/cancellation-due-death';
import { memo } from 'react';
import PmiTextField from 'shell/components/shared/text-field';
import { DatePicker } from '@mui/lab';
import { FormikErrors, FormikTouched } from 'formik';
import { PmiTitleDivider } from 'shell/components/shared/pmi-title-divider';
import CourtTypeList from './court-type-list';
import FormItalianAuthority from './form-italian-authority';
import FormForeignAuthority from './form-foreign-authority';
import { ItalianMunicipality } from 'shell/components/shared/event-form/form-italy-event';
import { ForeignMunicipality } from 'shell/components/shared/event-form/form-foreign-event';

interface JudgmentDataFormProps {
  judgmentDataForm?: JudgmentDataForm;
  errors: FormikErrors<JudgmentDataForm> | undefined;
  touched: FormikTouched<JudgmentDataForm> | undefined;
  handleFormFieldChange: (
    key: keyof JudgmentDataForm,
    value: JudgmentDataForm[keyof JudgmentDataForm]
  ) => void;
  disabled?: boolean;
}

const JudgmentDataFormComponent = ({
  judgmentDataForm,
  errors,
  touched,
  handleFormFieldChange,
  disabled = false,
}: JudgmentDataFormProps): React.ReactElement => {
  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <PmiTitleDivider>Dati Sentenza</PmiTitleDivider>
      </Grid>
      <Grid item xs={12}>
        <Divider />
      </Grid>
      <Grid item container xs={12} spacing={1}>
        <Grid item xs={12} md={4}>
          <PmiTextField
            id="judgmentNumber"
            label="Numero sentenza"
            aria-label="Numero sentenza"
            fullWidth
            onChange={(event) => handleFormFieldChange('judgmentNumber', event.target.value)}
            value={judgmentDataForm?.judgmentNumber}
            error={touched?.judgmentNumber && Boolean(errors?.judgmentNumber)}
            helperText={errors?.judgmentNumber}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data sentenza"
            aria-label="Data sentenza"
            value={judgmentDataForm?.judgmentDate || null}
            onChange={(date) => {
              handleFormFieldChange('judgmentDate', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                id="judgmentDate"
                {...params}
                fullWidth
                error={touched?.judgmentDate && Boolean(errors?.judgmentDate)}
                helperText={errors?.judgmentDate}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DatePicker
            label="Data validità"
            aria-label="Data validità"
            value={judgmentDataForm?.validityDate || null}
            onChange={(date) => {
              handleFormFieldChange('validityDate', date);
            }}
            disabled={disabled}
            renderInput={(params) => (
              <PmiTextField
                id="validityDate"
                {...params}
                fullWidth
                error={touched?.validityDate && Boolean(errors?.validityDate)}
                helperText={errors?.validityDate}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <PmiTextField
            id="authority"
            label="Autorità"
            aria-label="Autorità"
            fullWidth
            onChange={(event) => handleFormFieldChange('authority', event.target.value)}
            value={judgmentDataForm?.authority}
            error={touched?.authority && Boolean(errors?.authority)}
            helperText={errors?.authority}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <CourtTypeList
            label="Tipo tribunale"
            aria-label="Tipo tribunale"
            value={judgmentDataForm?.courtType || ''}
            onChange={(type) => handleFormFieldChange('courtType', type)}
            error={touched?.courtType && Boolean(errors?.courtType)}
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset">
            <RadioGroup
              row
              aria-label="Evento"
              id="happeInItaly"
              value={judgmentDataForm?.isItalianAuthority || 'false'}
              onChange={() =>
                handleFormFieldChange('isItalianAuthority', !judgmentDataForm?.isItalianAuthority)
              }
            >
              <FormControlLabel
                disabled={disabled}
                value="true"
                control={<Radio />}
                label="Autorità italiana"
              />
              <FormControlLabel
                disabled={disabled}
                value="false"
                control={<Radio />}
                label="Autorità estera"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        <Grid item xs={12} container spacing={1}>
          {judgmentDataForm?.isItalianAuthority ? (
            <FormItalianAuthority
              form={judgmentDataForm?.italianAuthority}
              fromDate={judgmentDataForm?.judgmentDate}
              handleChange={(italianAuthority) =>
                handleFormFieldChange('italianAuthority', italianAuthority)
              }
              errors={errors?.italianAuthority as FormikErrors<ItalianMunicipality>}
              touched={touched?.italianAuthority as unknown as FormikTouched<ItalianMunicipality>}
              disabled={disabled}
            />
          ) : (
            <FormForeignAuthority
              form={judgmentDataForm?.foreignAuthority}
              handleChange={(foreignAuthority) =>
                handleFormFieldChange('foreignAuthority', foreignAuthority)
              }
              errors={errors?.foreignAuthority as FormikErrors<ForeignMunicipality>}
              touched={touched?.foreignAuthority as unknown as FormikTouched<ForeignMunicipality>}
              disabled={disabled}
            />
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

JudgmentDataFormComponent.displayName = 'Judgment data form component';

const JudgmentDataFormComponentMemo = memo(JudgmentDataFormComponent);

export default JudgmentDataFormComponentMemo;
